import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ConversationData,
  LocationConditionEvaluationTree,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { processPlaybooksLocationSettings } from '../helpers/playbook-processing'
import { LocationData } from '../types'
import {
  useLocationSettings,
  usePlaybooksSelectWithConversation,
} from '../api-queries/chat-platform-chat'
import { getStoredSession } from './helpers'

export const useSelectPlaybook = ({
  tenantId,
  workspaceId,
  guestId,
  originUrl,
}: {
  tenantId: string
  workspaceId: string
  guestId: string
  originUrl: string
}) => {
  const {
    mutateAsync: playbooksSelectWithConversationAsync,
    isPending: isLoading,
    error,
  } = usePlaybooksSelectWithConversation(tenantId)
  return {
    selectConversation: useCallback(
      (
        possiblePlaybookIds: Array<string>,
        forceStartNextSession?: boolean,
        locationEvaluation?: Record<string, LocationConditionEvaluationTree>
      ) => {
        if (
          workspaceId &&
          guestId &&
          originUrl /* wait for url before matching because it could contain UTMs */
        ) {
          return playbooksSelectWithConversationAsync({
            guestId,
            playbookIds: possiblePlaybookIds,
            workspaceId,
            targetedUrl: originUrl,
            forceStartNextSession,
            locationEvaluation,
          })
        }
      },
      [guestId, originUrl, playbooksSelectWithConversationAsync, workspaceId]
    ),
    isLoading,
    error,
  }
}

type Result =
  | { type: 'RESUME_SESSION'; playbookId?: string }
  | { type: 'NEW_JOURNEY'; conversationData: Array<ConversationData>; playbookId: string }
  | undefined
export const useChatBootstrap = ({
  workspaceId,
  tenantId,
  guestId,
  locationData,
  forcedPlaybookId,
}: {
  forcedPlaybookId?: string
  guestId: string
  locationData: LocationData
  tenantId: string
  workspaceId: string
}): Result => {
  const triggered = useRef(false)
  const [selectedPlaybook, setSelectedPlaybook] = useState<
    { conversationData: Array<ConversationData>; playbookId: string } | undefined
  >()
  const isDone = useRef(false)
  const { data: locationSettings } = useLocationSettings({
    workspaceId,
    tenantId,
    guestId,
  })
  const {
    selectConversation,
    isLoading: isSelectingConversation,
    error,
  } = useSelectPlaybook({
    tenantId,
    workspaceId,
    guestId,
    originUrl: locationData.originUrl,
  })

  const selectPlaybook = useCallback(
    async (
      processedPlaybookIds: Array<string>,
      locationEvaluation?: Record<string, LocationConditionEvaluationTree>,
      forceStartNextSession?: boolean
    ) => {
      const selectedConversation = await selectConversation(
        processedPlaybookIds,
        forceStartNextSession,
        locationEvaluation
      )

      if (
        selectedConversation?.playbookId &&
        selectedConversation.conversationData &&
        !isDone.current
      ) {
        isDone.current = true
        setSelectedPlaybook({
          conversationData: selectedConversation.conversationData,
          playbookId: selectedConversation.playbookId,
        })
      }
    },
    [guestId, selectConversation]
  )

  const hasActiveSession = () => {
    if (!guestId || !workspaceId) {
      return
    }
    return getStoredSession(guestId, workspaceId)?.hasActiveSession
  }

  useEffect(() => {
    const playbookChagned = forcedPlaybookId && forcedPlaybookId !== selectedPlaybook?.playbookId
    if (playbookChagned && !isSelectingConversation && !error) {
      isDone.current = false
      triggered.current = true
    }
  }, [forcedPlaybookId, selectedPlaybook, isSelectingConversation, error])

  useEffect(() => {
    if (isDone.current || isSelectingConversation || error) {
      return
    }

    if (hasActiveSession() && !triggered.current) {
      isDone.current = true
      return
    }

    if (forcedPlaybookId) {
      selectPlaybook([forcedPlaybookId], undefined, triggered.current)
      triggered.current = false
      return
    }
    if (locationSettings) {
      const { playbookIds, locationEvaluation } = processPlaybooksLocationSettings(
        locationSettings.locationSettings,
        locationData
      )
      if (playbookIds.length > 0) {
        selectPlaybook(playbookIds, locationEvaluation)
      }
    }
  }, [
    locationData,
    selectPlaybook,
    locationSettings,
    guestId,
    workspaceId,
    selectedPlaybook,
    forcedPlaybookId,
    isSelectingConversation,
  ])

  if (selectedPlaybook) {
    return {
      type: 'NEW_JOURNEY',
      conversationData: selectedPlaybook.conversationData,
      playbookId: selectedPlaybook.playbookId,
    }
  }
  const session = getStoredSession(guestId, workspaceId)
  if (isDone.current && session) {
    return {
      type: 'RESUME_SESSION',
      playbookId: session.playbookId,
    }
  }
}
